import { DOCUMENT, Location } from '@angular/common';
import type {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type {
  AccountInfo,
  AuthenticationResult,
  IPublicClientApplication,
  PopupRequest,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { InteractionType, StringUtils } from '@azure/msal-browser';
import type { Observable } from 'rxjs';
import { EMPTY, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { ConfigurationService } from '#/app/shared/services/configuration.service';

import { IdentityProvider } from './identity-provider';

type MsalInterceptorAuthRequest =
  | Omit<PopupRequest, 'scopes'>
  | Omit<RedirectRequest, 'scopes'>
  | Omit<SilentRequest, 'scopes'>;

type ProtectedResourceMap = Map<
  string,
  (string | ProtectedResourceScopes)[] | null
>;

type MsalInterceptorAuthRequestFn = (
  msalService: IPublicClientApplication,
  req: HttpRequest<unknown>,
  originalAuthRequest: MsalInterceptorAuthRequest,
) => MsalInterceptorAuthRequest;

interface MsalInterceptorConfiguration {
  interactionType: InteractionType.Popup | InteractionType.Redirect;
  protectedResourceMap: ProtectedResourceMap;
  authRequest?: MsalInterceptorAuthRequest | MsalInterceptorAuthRequestFn;
}

export interface ProtectedResourceScopes {
  httpMethod: string;
  scopes: string[] | null;
}

@Injectable({ providedIn: 'root' })
export class MsalInterceptor implements HttpInterceptor {
  readonly #document = inject(DOCUMENT);
  readonly #location = inject(Location);
  readonly #identityProvider = inject(IdentityProvider);
  readonly #configurationService = inject(ConfigurationService);
  readonly #msalInterceptorConfig: MsalInterceptorConfiguration = {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
      ['/api/public/*', null], // no auth required
      ['/api/*', this.#configurationService.authConfig.entraId.scopes],
      ['https://graph.microsoft.com/v1.0/me', ['user.read']],
    ]) as ProtectedResourceMap,
    authRequest: (_msalService, _httpReq, originalAuthRequest) => {
      return originalAuthRequest;
    },
  };

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const currentMsalInstance = this.#identityProvider.instance.value();
    const scopes = this.#getScopesForEndpoint(req.url, req.method);

    // If no scopes for endpoint, does not acquire token
    if (!currentMsalInstance || !scopes || scopes.length === 0) {
      return next.handle(req);
    }

    // Sets account as active account or first account
    const account =
      currentMsalInstance.getActiveAccount() ??
      currentMsalInstance.getAllAccounts()[0];

    const authRequest =
      typeof this.#msalInterceptorConfig.authRequest === 'function'
        ? this.#msalInterceptorConfig.authRequest(currentMsalInstance, req, {
            account: account,
          })
        : { ...this.#msalInterceptorConfig.authRequest, account };

    return from(
      this.#acquireToken(authRequest, scopes, account, currentMsalInstance),
    ).pipe(
      switchMap((result: AuthenticationResult) => {
        const headers = req.headers.set(
          'Authorization',
          `Bearer ${result.accessToken}`,
        );

        const requestClone = req.clone({ headers });
        return next.handle(requestClone);
      }),
    );
  }

  /**
   * Try to acquire token silently. Invoke interaction if acquireTokenSilent rejected with error or resolved with null access token
   * @param authRequest Request
   * @param scopes Array of scopes for the request
   * @param account Account
   * @returns Authentication result
   */
  #acquireToken(
    authRequest: MsalInterceptorAuthRequest,
    scopes: string[],
    account: AccountInfo,
    currentMsalInstance: IPublicClientApplication,
  ): Observable<AuthenticationResult> {
    // Note: For MSA accounts, include openid scope when calling acquireTokenSilent to return idToken
    return from(
      currentMsalInstance.acquireTokenSilent({
        ...authRequest,
        scopes,
        account,
      }),
    ).pipe(
      catchError(() => {
        return this.#acquireTokenInteractively(
          authRequest,
          scopes,
          currentMsalInstance,
        );
      }),
      switchMap((result: AuthenticationResult) => {
        if (!result.accessToken) {
          return this.#acquireTokenInteractively(
            authRequest,
            scopes,
            currentMsalInstance,
          );
        }
        return of(result);
      }),
    );
  }

  /**
   * Invoke interaction for the given set of scopes
   * @param authRequest Request
   * @param scopes Array of scopes for the request
   * @returns Result from the interactive request
   */
  #acquireTokenInteractively(
    authRequest: MsalInterceptorAuthRequest,
    scopes: string[],
    currentMsalInstance: IPublicClientApplication,
  ): Observable<AuthenticationResult> {
    if (this.#msalInterceptorConfig.interactionType === InteractionType.Popup) {
      return from(
        currentMsalInstance.acquireTokenPopup({ ...authRequest, scopes }),
      );
    }
    const redirectStartPage = window.location.href;
    void currentMsalInstance.acquireTokenRedirect({
      ...authRequest,
      scopes,
      redirectStartPage,
    });
    return EMPTY;
  }

  /**
   * Looks up the scopes for the given endpoint from the protectedResourceMap
   * @param endpoint Url of the request
   * @param httpMethod Http method of the request
   * @returns Array of scopes, or null if not found
   *
   */
  #getScopesForEndpoint(endpoint: string, httpMethod: string): string[] | null {
    // Ensures endpoints and protected resources compared are normalized
    const normalizedEndpoint = this.#location.normalize(endpoint);

    const protectedResourcesArray = Array.from(
      this.#msalInterceptorConfig.protectedResourceMap.keys(),
    );

    const matchingProtectedResources = this.#matchResourcesToEndpoint(
      protectedResourcesArray,
      normalizedEndpoint,
    );

    if (matchingProtectedResources.length > 0) {
      return this.#matchScopesToEndpoint(
        this.#msalInterceptorConfig.protectedResourceMap,
        matchingProtectedResources,
        httpMethod,
      );
    }

    return null;
  }

  /**
   * Finds resource endpoints that match request endpoint
   * @param protectedResourcesEndpoints
   * @param endpoint
   * @returns
   */
  #matchResourcesToEndpoint(
    protectedResourcesEndpoints: string[],
    endpoint: string,
  ): string[] {
    const matchingResources: string[] = [];

    protectedResourcesEndpoints.forEach((key) => {
      const normalizedKey = this.#location.normalize(key);

      // Get url components
      const absoluteKey = this.#getAbsoluteUrl(normalizedKey);
      const keyComponents = new URL(absoluteKey);
      const absoluteEndpoint = this.#getAbsoluteUrl(endpoint);
      const endpointComponents = new URL(absoluteEndpoint);

      if (this.#checkUrlComponents(keyComponents, endpointComponents)) {
        matchingResources.push(key);
      }
    });

    return matchingResources;
  }

  /**
   * Compares URL segments between key and endpoint
   * @param key
   * @param endpoint
   * @returns
   */
  #checkUrlComponents(keyComponents: URL, endpointComponents: URL): boolean {
    // URL properties from https://developer.mozilla.org/en-US/docs/Web/API/URL
    const urlProperties = [
      'protocol',
      'host',
      'pathname',
      'search',
      'hash',
    ] as const;

    for (const property of urlProperties) {
      if (keyComponents[property]) {
        const decodedInput = decodeURIComponent(keyComponents[property]);
        if (
          !StringUtils.matchPattern(decodedInput, endpointComponents[property])
        ) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Transforms relative urls to absolute urls
   * @param url
   * @returns
   */
  #getAbsoluteUrl(url: string): string {
    const link = this.#document.createElement('a');
    link.href = url;
    return link.href;
  }

  /**
   * Finds scopes from first matching endpoint with HTTP method that matches request
   * @param protectedResourceMap Protected resource map
   * @param endpointArray Array of resources that match request endpoint
   * @param httpMethod Http method of the request
   * @returns
   */
  #matchScopesToEndpoint(
    protectedResourceMap: Map<
      string,
      (string | ProtectedResourceScopes)[] | null
    >,
    endpointArray: string[],
    httpMethod: string,
  ): string[] | null {
    const allMatchedScopes: (string[] | null)[] = [];

    // Check each matched endpoint for matching HttpMethod and scopes
    endpointArray.forEach((matchedEndpoint) => {
      const scopesForEndpoint: string[] | null = [];
      const methodAndScopesArray = protectedResourceMap.get(matchedEndpoint);

      // Return if resource is unprotected
      if (methodAndScopesArray === null) {
        allMatchedScopes.push(null);
        return;
      }

      methodAndScopesArray?.forEach((entry) => {
        // Entry is either array of scopes or ProtectedResourceScopes object
        if (typeof entry === 'string') {
          scopesForEndpoint.push(entry);
        } else {
          // Ensure methods being compared are normalized
          const normalizedRequestMethod = httpMethod.toLowerCase();
          const normalizedResourceMethod = entry.httpMethod.toLowerCase();
          // Method in protectedResourceMap matches request http method
          if (normalizedResourceMethod === normalizedRequestMethod) {
            // Validate if scopes comes null to unprotect the resource in a certain http method
            if (entry.scopes === null) {
              allMatchedScopes.push(null);
            } else {
              entry.scopes.forEach((scope) => {
                scopesForEndpoint.push(scope);
              });
            }
          }
        }
      });

      // Only add to all scopes if scopes for endpoint and method is found
      if (scopesForEndpoint.length > 0) {
        allMatchedScopes.push(scopesForEndpoint);
      }
    });

    if (allMatchedScopes.length > 0) {
      // Returns scopes for first matching endpoint
      return allMatchedScopes[0];
    }

    return null;
  }
}
